import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { SSAARenderPass } from 'three/examples/jsm/postprocessing/SSAARenderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import fragmentShader from '../../../shaders/distortion-slider/fragment.glsl';
import vertexShader from '../../../shaders/distortion-slider/vertex.glsl';

class WebGLDistortionSlider {
  constructor(options = {}) {
    this.options = {
      parent: options.parent,
      canvas: options.canvas,
      cardWidth: options.cardWidth || 390,
      cardHeight: options.cardHeight || 390,
      secondaryCardScale: options.secondaryCardScale || 1,
      slideDelay: options.slideDelay || 3000,
      slideSpeed: options.slideSpeed || 1.2,
      initialDelay: options.initialDelay || 100,
      slidePosDelta: options.slidePosDelta || 0,
      autoplay: options.autoplay !== undefined ? options.autoplay : false,
      scenePadding: options.scenePadding || 50
    };

    this.images = window.sliderImages || [];
    if (!this.images.length) {
      console.error('No images provided for slider');
      return;
    }

    this.currentIndex = 0;
    this.isAnimating = false;
    this.animationStartTime = null;
    this.animationDuration = (options.slideSpeed || 1.2) * 1000;

    this.texturesPromise = this.loadTextures();

    const totalPadding = this.options.scenePadding * 2;
    this.sideCardWidth = Math.ceil(this.options.cardWidth * this.options.secondaryCardScale);
    this.cardOffset = this.sideCardWidth / 2 * this.options.slidePosDelta;
    this.sliderWidth = this.options.cardWidth + this.sideCardWidth * 2 - this.cardOffset * 2;
    this.canvasWidth = this.sliderWidth;
    this.canvasHeight = this.options.cardHeight + totalPadding;
    this.paddingRatio = this.options.scenePadding / this.options.cardHeight;

    this.nextButton = document.querySelector('#button-next');
    this.prevButton = document.querySelector('#button-prev');

    this.bindAll();
    this.createProgressBar();

    this.init();
  }

  bindAll() {
    this.render = this.render.bind(this);
  }

  init() {
    this.setupScene();

    this.texturesPromise.then(() => {
      this.createSlides();
      this.setupEvents();
      this.render();

      requestAnimationFrame(() => {
        if (this.options.parent) {
          this.options.parent.classList.add('active');
        }
        if (this.options.autoplay) {
          this.startSlideshow();
        }
      });
    }).catch(error => {
      console.error('Failed to initialize slider:', error);
    });
  }

  setupScene() {
    this.scene = new THREE.Scene();

    this.verticalUnits = 2;
    const aspectRatio = this.canvasWidth / this.canvasHeight;
    console.log('aspectRatio', aspectRatio)

    const paddingUnits = this.paddingRatio * this.verticalUnits;

    const totalHorizontalUnits = aspectRatio * (this.verticalUnits + paddingUnits * 2);

    this.camera = new THREE.OrthographicCamera(
      -totalHorizontalUnits / 2,
      totalHorizontalUnits / 2,
      (this.verticalUnits + paddingUnits * 2) / 2,
      -(this.verticalUnits + paddingUnits * 2) / 2,
      0.1,
      10
    );
    this.camera.position.z = 1;

    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: true,
      powerPreference: 'high-performance',
      stencil: false,
      useLegacyLights: false,
      physicallyCorrectLights: false,
      canvas: this.options.canvas
    });
    this.renderer.setSize(this.canvasWidth, this.canvasHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.NoToneMapping

    this.composer = new EffectComposer(this.renderer);
    this.composer.setPixelRatio(1);

    this.saaRenderPass = new SSAARenderPass(this.scene, this.camera);
    this.saaRenderPass.sampleLevel = 16;
    this.saaRenderPass.unbiased = true;
    this.composer.addPass(this.saaRenderPass);

    this.outputPass = new OutputPass();
    this.composer.addPass(this.outputPass);
  }

  createProgressBar() {
    this.progressBarContainer = document.createElement('div');
    this.progressBarContainer.className = 'slider-progress-container';

    this.progressBar = document.createElement('div');
    this.progressBar.className = 'slider-progress-bar';
    this.progressBar.style.setProperty('--slider-transition-duration', `${this.animationDuration}ms`);

    this.progressBarContainer.appendChild(this.progressBar);

    this.options.canvas.parentNode.appendChild(this.progressBarContainer);
    this.updateProgressBar();
  }

  updateProgressBar(isLastToFirst = false) {
    if (!this.progressBar) return;

    const totalSlides = this.images.length;
    if (totalSlides <= 1) {
      this.progressBar.style.transform = 'scaleX(1)';
      return;
    }

    if (isLastToFirst) {
      this.progressBar.style.transformOrigin = 'right';
      this.progressBar.style.transform = 'scaleX(0)';

      setTimeout(() => {
        this.progressBar.style.transition = 'none';
        this.progressBar.style.transformOrigin = 'left';
        this.progressBar.style.transform = 'scaleX(0)';
        this.progressBar.offsetHeight;
        this.progressBar.style.transition = `transform ${this.animationDuration}ms ease-out`;
      }, this.animationDuration);
    } else {
      const progress = this.currentIndex / (totalSlides - 1);
      this.progressBar.style.transform = `scaleX(${progress})`;
    }
  }

  getTextureIndex(offset, direction = 0) {
    const total = this.textures.length;

    if (direction === 0) {
      return (this.currentIndex + offset + total) % total;
    } else {
      return ((this.currentIndex - offset) + total) % total;
    }
  }

  nextSlide(direction = 0) {
    if (this.isAnimating) return;

    const nextSlideDelta = direction === 0 ? 1 : -1;
    let nextIndex = (this.currentIndex + nextSlideDelta + this.textures.length) % this.textures.length;

    let attempts = this.textures.length;
    while (attempts > 0 && !this.isTextureLoaded(nextIndex)) {
      nextIndex = (nextIndex + nextSlideDelta + this.textures.length) % this.textures.length;
      attempts--;
    }

    if (!this.isTextureLoaded(nextIndex)) return;

    this.isAnimating = true;

    this.materials.forEach((material, index) => {
      let nextImageIndex;
      if(direction === 0) {
        nextImageIndex = this.getTextureIndex(index, direction);
      } else {
        nextImageIndex = this.getTextureIndex(2 - index, direction);
      }

      if (!this.isTextureLoaded(nextImageIndex)) return;

      material.uniforms.uNextImage.value = this.textures[nextImageIndex];
      material.uniforms.uDirection.value = direction;
    });

    this.animationStartTime = performance.now();

    const isLastToFirst = direction === 0 && this.currentIndex === this.textures.length - 1;

    this.currentIndex = nextIndex;
    this.updateProgressBar(isLastToFirst);

    this.animateProgress(direction, nextIndex);
  }

  isTextureLoaded(index) {
    const texture = this.textures[index];
    return texture && texture.image && !texture.loadFailed;
  }

  animateProgress(direction, nextIndex) {
    const currentTime = performance.now();
    const elapsed = currentTime - this.animationStartTime;
    const progress = Math.min(elapsed / this.animationDuration, 1);

    const easeOutProgress = 1 - Math.pow(2, -10 * progress);

    this.materials.forEach(material => {
      material.uniforms.uProgress.value = easeOutProgress;
    });

    if (progress < 1) {
      requestAnimationFrame(() => this.animateProgress(direction, nextIndex));
    } else {
      this.materials.forEach(material => {
        material.uniforms.uProgress.value = 0;
      });

      this.updateSlideTextures(direction);
      this.isAnimating = false;
    }
  }

  updateSlideTextures(direction = 0) {
    const total = this.textures.length;

    this.materials.forEach((material, index) => {
      let currentImageIndex, nextImageIndex;

      if (direction === 0) {
        currentImageIndex = this.getTextureIndex(index - 1, direction);
      } else {
        currentImageIndex = this.getTextureIndex(1 - index, direction);
      }

      material.uniforms.uCurrentImage.value = this.textures[currentImageIndex];
    });
  }

  prevSlide() {
    this.nextSlide(1);
  }

  loadTextures() {
    return new Promise((resolve, reject) => {
      if (!this.images.length) {
        reject(new Error('No images provided'));
        return;
      }

      this.textures = [];
      const loadManager = new THREE.LoadingManager();
      const loader = new THREE.TextureLoader(loadManager);
      let loadedCount = 0;
      let errorCount = 0;

      loadManager.onLoad = () => {
        if (loadedCount > 0) {
          resolve();
        } else {
          reject(new Error('No textures were loaded successfully'));
        }
      };

      loadManager.onError = (url) => {
        console.error('Error loading texture:', url);
        errorCount++;
      };

      this.images.forEach((imagePath, index) => {
        const texture = loader.load(
          imagePath,
          () => {
            loadedCount++;
            texture.loadedIndex = index;
          },
          undefined,
          (error) => {
            console.error(`Error loading texture ${imagePath}:`, error);
            errorCount++;
            texture.loadFailed = true;
          }
        );

        texture.generateMipmaps = false;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.anisotropy = this.renderer?.capabilities.getMaxAnisotropy() || 1;
        texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.needsUpdate = true;

        this.textures.push(texture);
      });
    });
  }

  createSlides() {
    const cardAspect = this.options.cardWidth / this.options.cardHeight;
    const cardWidth = this.verticalUnits * cardAspect;

    const geometry = new THREE.PlaneGeometry(cardWidth, this.verticalUnits);

    this.sharedMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uCurrentImage: { value: null },
        uNextImage: { value: null },
        uProgress: { value: 0 },
        uDirection: { value: 0 },
        uDistortionScaleIn: { value: 2.4 },
        uDistortionScaleOut: { value: 1.9 },
        uOverlayColor: { value: new THREE.Vector4(0, 0, 0, 0.2) },
        uHasOverlay: { value: 0 }
      },
      vertexShader,
      fragmentShader,
      transparent: false,
      side: THREE.DoubleSide
    })

    this.materials = Array(3).fill().map((_, index) => {
      return this.sharedMaterial.clone();
    });

    const positions = [-this.verticalUnits * cardAspect, 0, this.verticalUnits * cardAspect];
    const positionsOffset = [this.options.slidePosDelta, 0, -this.options.slidePosDelta];
    this.slides = positions.map((x, i) => {
      const mesh = new THREE.Mesh(geometry, this.materials[i]);
      mesh.position.x = x + positionsOffset[i];
      mesh.position.z = i === 1 ? .1 : .0;
      const currentImageIndex = this.getTextureIndex(i - 1, 0);
      const nextImageIndex = this.getTextureIndex(i, 0);
      this.materials[i].uniforms.uCurrentImage.value = this.textures[currentImageIndex];
      this.materials[i].uniforms.uNextImage.value = this.textures[nextImageIndex];
      this.materials[i].uniforms.uHasOverlay.value = i === 1 ? 0 : 1;

      if (i !== 1) {
        mesh.scale.set(this.options.secondaryCardScale, this.options.secondaryCardScale, 1);
      }

      this.scene.add(mesh);
      return mesh;
    });
  }

  setupEvents() {
    window.addEventListener('resize', this.onResize.bind(this));

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        if (this.slideInterval) {
          this.stopSlideshow();
        }
        this.nextSlide(0);
      });
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        if (this.slideInterval) {
          this.stopSlideshow();
        }
        this.nextSlide(1);
      });
    }
  }

  onResize() {
    const paddingUnits = this.paddingRatio * this.verticalUnits * 2;
    const aspectRatio = this.canvasWidth / this.canvasHeight;
    const totalHorizontalUnits = aspectRatio * (this.verticalUnits + paddingUnits);

    this.camera.left = -totalHorizontalUnits / 2;
    this.camera.right = totalHorizontalUnits / 2;
    this.camera.top = (this.verticalUnits + paddingUnits) / 2;
    this.camera.bottom = -(this.verticalUnits + paddingUnits) / 2;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(this.canvasWidth, this.canvasHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  render() {
    requestAnimationFrame(this.render.bind(this));
    // this.renderer.render(this.scene, this.camera);
    this.composer.render();
  }

  startSlideshow() {
    if (!this.textures.length || this.slideInterval) return;

    this.slideInterval = setInterval(() => {
      this.nextSlide(0);
    }, this.options.slideDelay);
  }

  stopSlideshow() {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.slideInterval = null;
    }
  }

  destroy() {
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.nextSlide);
    }
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.prevSlide);
    }
    window.removeEventListener('resize', this.onResize);

    this.stopSlideshow();

    if (this.progressBarContainer) {
      this.progressBarContainer.remove();
    }

    this.renderer.dispose();
    this.materials.forEach(material => material.dispose());
    this.textures.forEach(texture => texture.dispose());
  }
}

export default WebGLDistortionSlider;
